/**
 * Audit log categories - fixed set of top-level categorizations
 */
export enum AuditLogCategory {
  DEVICE = 'Device',
  AUTHENTICATION = 'Authentication',
  ACCESS_MANAGEMENT = 'Access Management',
  AUDIT = 'Audit',
  CONFIGURATION = 'Configuration',
  DATA_MANAGEMENT = 'Data Management',
  ADMIN_MANAGEMENT = 'Admin Management',
  LICENSING = 'Licensing',
}

export interface AggregationStatistics {
  totalAttempted?: number;
  totalSucceeded?: number;
  totalFailed?: number;
  devicesSynced?: number;
  usersSynced?: number;
  groupingsSynced?: number;
}

export interface AuditLogMessage {
  customerId?: number;
  /**
   * Database user ID from the users table
   * This is the foreign key reference to the users table
   */
  userId?: number;
  category?: AuditLogCategory;
  operationType?: string;
  action?: string;
  actionData?: string;
  jobId?: string | null;
  jobExecutionId?: string | null;
  hasErrors?: boolean | null;
  aggregationStatistics?: AggregationStatistics | null;
  /**
   * Which asset type this log's changes are about, denormalized onto audit_logs
   * from audit_log_asset_changes. NULL when the job produced no asset changes
   * (exports, cache refreshes) — the client uses that to decide whether a
   * detail view exists.
   */
  assetTypeId?: number | null;
  createdAt?: Date;
  updatedAt?: Date;
}

export type AuditLogMessageRecord = { id: number } & AuditLogMessage;

/**
 * Audit log record with user information for frontend display
 */
export interface AuditLogWithUser extends AuditLogMessageRecord {
  user?: {
    id: number;
    name: string;
    email: string;
    role?: string;
  };
  logCount?: number;
  /**
   * Export output pointer joined from jobs.additional_job_data.
   * Present only for completed export jobs — NULL for non-export bulk jobs
   * (device updates, profile wipes, etc.) and for single-action logs.
   * For google-sheets format this is the spreadsheet URL; for csv it is the
   * GCS path (frontend routes CSV through /open/export/:jobId to trigger download).
   */
  outputReference?: string | null;
  outputFormat?: string | null;
  /**
   * Joined from jobs.additional_job_data: the selection size and the devices
   * skipped as ineligible before the job ran (see EligibilitySkipSummary).
   * NULL for jobs that skip nothing and for jobs created before these were
   * recorded.
   */
  requestedCount?: number | null;
  skippedCount?: number | null;
  /**
   * EDUS-3090: the job run was cancelled while in progress. Derived from
   * jobs_status rather than stored, because a cancel stops the workflow before
   * write-aggregation-statistics, so aggregationStatistics stays null.
   */
  cancelled?: boolean;
}

/**
 * Interface for creating audit logs from the frontend
 * Extends AuditLogMessage but makes required fields explicit
 */
export interface CreateAuditLogDto extends Partial<AuditLogMessage> {
  /**
   * Database user ID - use core.dbUserId from claims
   * This is required for foreign key relationship to users table
   */
  userId: number;
  category: AuditLogCategory;
  operationType: string;
  action: string;
  actionData: string;
}

/**
 * Job types for multi-device operations only
 * Single device actions (single edits, in-cell edits) are NOT jobs
 */
export const JOB_TYPES = {
  DEVICE_EDIT_IMPORT: 'device-edit-import', // CSV import affecting multiple devices
  DEVICE_EDIT_BULK: 'device-edit-bulk', // Bulk edit affecting multiple devices
  DEVICE_DEPROVISION: 'device-deprovision', // Batch deprovision
  DEVICE_PROFILE_WIPE: 'device-profile-wipe', // Batch profile wipe
  DEVICE_FACTORY_RESET: 'device-factory-reset', // Batch factory reset
  DEVICE_ENABLE_DISABLE: 'device-enable-disable', // Batch enable/disable
  DEVICE_OU_MOVE: 'device-ou-move', // Batch OU move
  DEVICE_OU_SYNC_USER: 'device-ou-sync-user', // Batch OU sync with user's OU
  DEVICE_EXPORT_REPORTING: 'device-export-reporting', // Export for reporting (human-readable)
  DEVICE_EXPORT_BULK_EDIT: 'device-export-bulk-edit', // Export for bulk edit (import-ready)
} as const;

export const EXPORT_JOB_TYPES: ReadonlySet<string> = new Set([
  JOB_TYPES.DEVICE_EXPORT_REPORTING,
  JOB_TYPES.DEVICE_EXPORT_BULK_EDIT,
]);

export const SYNC_JOB_TYPES: ReadonlySet<string> = new Set([
  'full-asset-sync',
  'scheduled-asset-sync',
]);

/**
 * Single-device action types (not jobs, no jobId)
 */
export const SINGLE_ACTION_TYPES = {
  DEVICE_EDIT_SINGLE: 'device-edit-single', // Single device edit from report
  DEVICE_EDIT_INCELL: 'device-edit-incell', // Single in-cell edit
} as const;

/**
 * System action types (authentication, access management, configuration)
 */
export const SYSTEM_ACTION_TYPES = {
  // Authentication
  USER_LOGIN: 'user-login',
  USER_LOGOUT: 'user-logout',
  FAILED_LOGIN: 'failed-login',

  // Role Management
  ROLE_CREATED: 'role-created',
  ROLE_REMOVED: 'role-removed',

  // OU Collection Management
  OU_COLLECTION_CREATED: 'ou-collection-created',
  OU_COLLECTION_REMOVED: 'ou-collection-removed',

  // User Collection Management
  USER_COLLECTION_CREATED: 'user-collection-created',
  USER_COLLECTION_REMOVED: 'user-collection-removed',

  // Designation Management
  DESIGNATION_CREATED: 'designation-created',
  DESIGNATION_REMOVED: 'designation-removed',

  // Audit & Monitoring
  AUDIT_LOG_VIEWED: 'audit-log-viewed',

  // Admin Management
  ADMIN_CHANGED_TO_DELEGATED: 'admin-changed-to-delegated',
  ADMIN_CHANGED_TO_SUPER: 'admin-changed-to-super',

  // Configuration
  GOPHER_BUDDY_CONFIG_CHANGED: 'gopher-buddy-config-changed',
  GLOBAL_SETTINGS_CHANGED: 'global-settings-changed',
  TIMEZONE_SETTINGS_CHANGED: 'timezone-settings-changed',

  // Licensing
  LICENSING_CONTACT_ADDED: 'licensing-contact-added',
  LICENSING_CONTACT_REMOVED: 'licensing-contact-removed',

  // Data Management
  DATA_EXPORTED: 'data-exported',
  DATA_IMPORTED: 'data-imported',
} as const;
